import React, { useState } from 'react'
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Code,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  SimpleGrid,
  Text,
  useColorModeValue,
  useDisclosure,
  VisuallyHidden,
  VStack,
} from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { FiInfo, FiKey, FiSettings } from 'react-icons/fi'
import { SectionSkeleton } from '@/components/common/LoadingSkeleton'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'
import { useAuth } from '@/contexts/AuthContext'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import {
  type AdminCacheConfigResponse,
  type AdminCacheDebugResponse,
  CACHE_TOOL_QUERY_POLICY,
  getCacheConfig,
  getCacheDebugInfo,
  getCacheKeys,
} from '@/services/cacheService'
import { formatDateTime } from '@/utils/formatUtils'

const extractErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message
  }
  if (error && typeof error === 'object') {
    const candidate = error as {
      response?: { data?: { error?: { message?: unknown } } }
    }
    const message = candidate.response?.data?.error?.message
    if (typeof message === 'string' && message.trim()) {
      return message
    }
  }
  return fallback
}

const normalizeRole = (role?: string | null) => {
  const lower = role?.toLowerCase() ?? ''
  return {
    raw: lower,
    flat: lower.replace(/[-_]/g, ''),
    withUnderscore: lower.replace(/-/g, '_'),
  }
}

const CacheKeysModal: React.FC<{
  isOpen: boolean
  onClose: () => void
}> = ({ isOpen, onClose }) => {
  const { t } = useTranslation()
  const { currentEnv } = useNetworkEnvironment()
  const [draftPrefix, setDraftPrefix] = useState('')
  const [draftLimit, setDraftLimit] = useState(100)
  const [query, setQuery] = useState<{
    prefix: string
    limit: number
    cursor?: string
  }>({ prefix: '', limit: 100 })
  const {
    data,
    error,
    isLoading,
    isRefetching,
    refetch,
  } = useQuery({
    queryKey: ['admin', 'cacheTools', 'keys', currentEnv, query],
    queryFn: () => getCacheKeys(query),
    enabled: isOpen,
    ...CACHE_TOOL_QUERY_POLICY,
  })

  const handleSearch = () => {
    const next = { prefix: draftPrefix, limit: draftLimit }
    if (query.prefix === next.prefix && query.limit === next.limit && !query.cursor) {
      void refetch()
      return
    }
    setQuery(next)
  }

  const handleNextPage = () => {
    const cursor = data?.data.cursor
    if (!cursor) return
    setQuery(current => ({ ...current, cursor }))
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('monitoring.cache.cacheKeys')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <HStack spacing={3} align="end">
              <FormControl flex={1}>
                <FormLabel fontSize="sm">
                  {t('monitoring.cache.keyPrefix')}
                </FormLabel>
                <Input
                  size="sm"
                  placeholder={t('monitoring.cache.enterPrefix')}
                  value={draftPrefix}
                  onChange={event => setDraftPrefix(event.target.value)}
                />
              </FormControl>
              <FormControl maxW="120px">
                <FormLabel fontSize="sm">Limit</FormLabel>
                <NumberInput
                  size="sm"
                  value={draftLimit}
                  onChange={(_, value) => setDraftLimit(value)}
                  min={1}
                  max={1000}
                >
                  <NumberInputField />
                </NumberInput>
              </FormControl>
              <Button
                size="sm"
                colorScheme="admin"
                onClick={handleSearch}
                isDisabled={isLoading || isRefetching}
              >
                {t('monitoring.cache.searchKeys')}
              </Button>
            </HStack>
            <Divider />
            {isLoading ? (
              <Box py={8} role="status" aria-busy="true">
                <SectionSkeleton lines={4} withTitle={false} minHeight="160px" />
                <VisuallyHidden>{t('common.loading')}</VisuallyHidden>
              </Box>
            ) : error ? (
              <Alert status="warning">
                <AlertIcon />
                {extractErrorMessage(error, t('common.error'))}
              </Alert>
            ) : data ? (
              <>
                <Text fontSize="sm" color="gray.600">
                  {t('monitoring.cache.keysFound', { count: data.data.count })}
                </Text>
                {data.data.keys.length > 0 ? (
                  <Box
                    maxH="400px"
                    overflowY="auto"
                    borderWidth="1px"
                    borderRadius="md"
                    p={2}
                  >
                    <List spacing={1}>
                      {data.data.keys.map(key => (
                        <ListItem key={key.name}>
                          <HStack spacing={2} align="center">
                            <Code fontSize="xs" colorScheme="gray" flex="1">
                              {key.name}
                            </Code>
                            {key.expiration !== undefined && (
                              <Text fontSize="xs" color="gray.500">
                                {formatDateTime(key.expiration * 1000)}
                              </Text>
                            )}
                          </HStack>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ) : (
                  <Alert status="info">
                    <AlertIcon />
                    {t('monitoring.cache.noKeysFound')}
                  </Alert>
                )}
              </>
            ) : null}
          </VStack>
        </ModalBody>
        <ModalFooter gap={2}>
          {data && !data.data.complete && (
            <Button
              onClick={handleNextPage}
              isDisabled={!data.data.cursor || isLoading || isRefetching}
              colorScheme="admin"
              variant="outline"
            >
              {t('monitoring.cache.nextPage')}
            </Button>
          )}
          <Button onClick={onClose}>{t('common.close')}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

const CacheDetails: React.FC<{
  data: AdminCacheConfigResponse | AdminCacheDebugResponse
  showBindings: boolean
}> = ({ data, showBindings }) => {
  const { t } = useTranslation()
  const bgColor = useColorModeValue('gray.50', 'gray.700')
  const configEntries = (['merchant', 'order', 'user'] as const).map(service => ({
    service,
    value: data.config[service],
  }))
  const debug = showBindings ? data as AdminCacheDebugResponse : undefined

  return (
    <VStack spacing={4} align="stretch">
      <Box bg={bgColor} p={4} borderRadius="md">
        <Heading size="sm" mb={3}>
          {t('monitoring.cache.environment')}
        </Heading>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <HStack>
            <Text fontSize="sm" fontWeight="medium">
              {t('monitoring.cache.mode')}:
            </Text>
            <Badge>{data.mode}</Badge>
          </HStack>
          <HStack>
            <Text fontSize="sm" fontWeight="medium">
              {t('monitoring.cache.environment')}:
            </Text>
            <Code fontSize="xs">{data.environment}</Code>
          </HStack>
          <HStack>
            <Text fontSize="sm" fontWeight="medium">
              {t('monitoring.cache.kvNamespace')}:
            </Text>
            <Code fontSize="xs">{data.config.global.kvNamespace}</Code>
          </HStack>
          <HStack>
            <Text fontSize="sm" fontWeight="medium">
              {t('monitoring.cache.lastUpdated')}:
            </Text>
            <Text fontSize="sm">{formatDateTime(data.timestamp)}</Text>
          </HStack>
        </SimpleGrid>
      </Box>

      {debug && (
        <Box bg={bgColor} p={4} borderRadius="md">
          <Heading size="sm" mb={3}>
            {t('monitoring.cache.bindings')}
          </Heading>
          <VStack spacing={2} align="stretch">
            {Object.values(debug.bindings).map(binding => (
              <Flex key={binding.name} justify="space-between" gap={3}>
                <Code fontSize="xs">{binding.name}</Code>
                <Badge colorScheme={binding.configured ? 'green' : 'red'}>
                  {t(
                    binding.configured
                      ? 'monitoring.cache.configured'
                      : 'monitoring.cache.notConfigured'
                  )}
                </Badge>
              </Flex>
            ))}
          </VStack>
        </Box>
      )}

      <Box bg={bgColor} p={4} borderRadius="md">
        <Heading size="sm" mb={3}>
          {t('monitoring.cache.runtimeConfig')}
        </Heading>
        <VStack spacing={3} align="stretch">
          {configEntries.map(({ service, value }) => (
            <Box key={service}>
              <Flex justify="space-between" gap={3} wrap="wrap">
                <Text fontSize="sm" fontWeight="semibold">
                  {t(`monitoring.cache.${service}`)}
                </Text>
                <HStack>
                  <Badge>{value.ttl}s</Badge>
                  <Badge colorScheme={value.enabled ? 'green' : 'gray'}>
                    {t(
                      value.enabled
                        ? 'monitoring.cache.enabled'
                        : 'monitoring.cache.disabled'
                    )}
                  </Badge>
                </HStack>
              </Flex>
              <Text fontSize="xs" color="gray.600" mt={1}>
                {value.description}
              </Text>
            </Box>
          ))}
        </VStack>
      </Box>
    </VStack>
  )
}

const CacheDetailsModal: React.FC<{
  kind: 'config' | 'debug'
  isOpen: boolean
  onClose: () => void
}> = ({ kind, isOpen, onClose }) => {
  const { t } = useTranslation()
  const { currentEnv } = useNetworkEnvironment()
  const {
    data,
    error,
    isLoading,
    isRefetching,
    refetch,
  } = useQuery({
    queryKey: ['admin', 'cacheTools', kind, currentEnv],
    queryFn: () => kind === 'config'
      ? getCacheConfig(currentEnv)
      : getCacheDebugInfo(currentEnv),
    enabled: isOpen,
    ...CACHE_TOOL_QUERY_POLICY,
  })

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t(
            kind === 'config'
              ? 'monitoring.cache.configInfoTitle'
              : 'monitoring.cache.debugInfoTitle'
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isLoading ? (
            <Box py={8} role="status" aria-busy="true">
              <SectionSkeleton lines={4} withTitle={false} minHeight="160px" />
              <VisuallyHidden>{t('common.loading')}</VisuallyHidden>
            </Box>
          ) : error ? (
            <Alert status="warning">
              <AlertIcon />
              {extractErrorMessage(error, t('common.error'))}
            </Alert>
          ) : data ? (
            <CacheDetails data={data} showBindings={kind === 'debug'} />
          ) : null}
        </ModalBody>
        <ModalFooter gap={2}>
          <RefreshIconButton
            label={t('common.refresh')}
            isLoading={isRefetching}
            onClick={() => refetch()}
          />
          <Button onClick={onClose}>{t('common.close')}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export const CacheMonitor: React.FC = () => {
  const { t } = useTranslation()
  const { currentEnv } = useNetworkEnvironment()
  const pageBg = useColorModeValue('gray.50', 'gray.900')
  const headerBg = useColorModeValue('white', 'gray.800')
  const keys = useDisclosure()
  const config = useDisclosure()
  const debug = useDisclosure()
  const { checkPermission, user } = useAuth()
  const normalizedRoleId = normalizeRole(user?.role_id)
  const normalizedRoleCode = normalizeRole(user?.role_code)
  const isSuperAdmin = [normalizedRoleId, normalizedRoleCode].some(
    ({ raw, flat, withUnderscore }) =>
      raw === 'superadmin' ||
      raw === 'arl_superadmin' ||
      withUnderscore === 'arl_superadmin' ||
      flat === 'superadmin' ||
      flat === 'arlsuperadmin'
  )
  const canRead = isSuperAdmin || checkPermission('cache:read')

  if (!canRead) {
    return (
      <Box bg={pageBg} p={6} borderRadius="md">
        <Alert status="warning">
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">
              {t('monitoring.cache.noReadPermission')}
            </Text>
            <Text fontSize="sm">{t('monitoring.cache.contactAdmin')}</Text>
          </Box>
        </Alert>
      </Box>
    )
  }

  return (
    <Box>
      <Box bg={headerBg} px={6} py={5} borderRadius="lg" shadow="sm">
        <Flex justify="space-between" align="center" gap={4} wrap="wrap">
          <Box flex={{ base: '1 1 100%', md: '1' }} minW={0}>
            <Heading size="md">{t('monitoring.cache.title')}</Heading>
            <Text color="gray.500" fontSize="sm" mt={1}>
              {t('monitoring.cache.description', { mode: currentEnv })}
            </Text>
          </Box>
          <Flex
            gap={2}
            direction={{ base: 'column', sm: 'row' }}
            align={{ base: 'stretch', sm: 'center' }}
            w={{ base: 'full', md: 'auto' }}
          >
            <Button leftIcon={<FiKey />} onClick={keys.onOpen} size="sm">
              {t('monitoring.cache.viewCacheKeys')}
            </Button>
            <Button leftIcon={<FiSettings />} onClick={config.onOpen} size="sm">
              {t('monitoring.cache.configInfo')}
            </Button>
            <Button leftIcon={<FiInfo />} onClick={debug.onOpen} size="sm">
              {t('monitoring.cache.debugInfo')}
            </Button>
          </Flex>
        </Flex>
      </Box>

      <CacheKeysModal isOpen={keys.isOpen} onClose={keys.onClose} />
      <CacheDetailsModal
        kind="config"
        isOpen={config.isOpen}
        onClose={config.onClose}
      />
      <CacheDetailsModal
        kind="debug"
        isOpen={debug.isOpen}
        onClose={debug.onClose}
      />
    </Box>
  )
}
